import {
  ChecklistItemStatusType,
  FormAnswerUpdateRequest,
  FormElementExtended,
  FormElementQuestionType,
  InapplicableBoolAnswer,
  KnownBoolAnswer,
  UnknownBoolAnswer,
} from "@kolibrisoftware/customerportal-api";
import {
  CheckboxInput,
  DateInput,
  FormElementChangeCallback,
  NumberInput,
  Organisms,
  RadioGroup,
  TextInput,
  TextareaInput,
} from "@kolibrisoftware/customerportal-ui";
import classnames from "classnames";
import { format, parseISO } from "date-fns";
import useInaplicableBoolOptions from "hooks/useInapplicableBoolOptions";
import useKnownBoolOptions from "hooks/useKnownBoolOptions";
import useUnknownBoolOption from "hooks/useUnkownBoolOptions";
import { useTheme } from "injectors/theme";
import { isBoolean, isDate, isEmpty, isNumber, isString } from "lodash-es";
import useQuestionAnswer from "modules/questionnaire/hooks/useElementAnswer";
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "store/helpers";
import { thunks as documentThunks } from "store/questionnaire";
import { useStyles } from "./styles";

type Props = {
  questionDetails: FormElementExtended;
  isRadioLayout?: boolean;
};

const QuestionInput: FC<Props> = ({ questionDetails, isRadioLayout }) => {
  const theme = useTheme();
  const styles = useStyles({ theme });
  const dispatch = useDispatch();
  const outsideQuestionAnswer = useQuestionAnswer(questionDetails.id);
  const { formEditableStatus } = useSelector(state => state.questionnaire);
  const unknownBoolOptions = useUnknownBoolOption();
  const knownBoolOptions = useKnownBoolOptions();
  const inaplicableBoolOptions = useInaplicableBoolOptions();

  const [questionAnswer, setQuestionAnswer] = useState(outsideQuestionAnswer);

  const onAnswerSubmit = useCallback(
    (value: string | number | boolean | Date | Array<string>) => {
      const val = value;
      const checked = value as boolean;

      if (!questionAnswer || !questionAnswer.id) {
        throw new Error("Missing answer id");
      }

      const updatedAnswer: FormAnswerUpdateRequest = {
        ...questionAnswer,
        formAnswerId: questionAnswer.id,
      };

      switch (questionDetails.questionType) {
        case FormElementQuestionType.Text:
          if (isString(val)) {
            updatedAnswer.textAnswer = val;
          }
          break;
        case FormElementQuestionType.TextArea:
          if (isString(val)) {
            updatedAnswer.textAreaAnswer = val;
          }
          break;
        case FormElementQuestionType.Radio:
          updatedAnswer.radioAnswer = questionDetails.questionOptions?.find(
            item => item.translations?.[0].value === val
          )?.id;
          break;
        case FormElementQuestionType.InapplicableBool:
          updatedAnswer.inapplicableBoolOption = val as InapplicableBoolAnswer;
          break;
        case FormElementQuestionType.UnknownBool:
          updatedAnswer.unknownBoolOption = val as UnknownBoolAnswer;
          break;
        case FormElementQuestionType.KnownBool:
          updatedAnswer.knownBoolOption = val as KnownBoolAnswer;
          break;
        case FormElementQuestionType.Bool:
          if (isBoolean(checked)) {
            updatedAnswer.boolAnswer = checked;
          }
          break;
        case FormElementQuestionType.Date:
          if (!isDate(val) && isString(val) && val) {
            const answer = new Date(val);
            updatedAnswer.dateAnswer = answer;
          }
          break;
        case FormElementQuestionType.DateTime:
          if (!isDate(val) && isString(val) && val) {
            const answer = new Date(val);
            updatedAnswer.dateTimeAnswer = answer;
          }
          break;
        case FormElementQuestionType.Decimal:
          const parsedFloat = parseFloat(val as string);
          if (isNumber(parsedFloat)) {
            updatedAnswer.decimalAnswer = parsedFloat;
          }
          break;
        case FormElementQuestionType.Int:
          const parsedInt = parseInt(val as string, 10);
          if (isNumber(parsedInt)) {
            updatedAnswer.intAnswer = parsedInt;
          }
          break;
        case FormElementQuestionType.MultiSelect:
          const multiSelectAnswer = Array.isArray(val) ? val : [val];
          updatedAnswer.multiSelectAnswerOptionList =
            multiSelectAnswer as string[];
          break;
        default:
          break;
      }

      const isRequiredAndEmpty =
        questionDetails.questionConstraint?.required && isEmpty(val);

      if (isRequiredAndEmpty) {
        return;
      }

      dispatch(
        documentThunks.setQuestionAnswer({
          questionId: questionDetails.id || "",
          updatedAnswerPayload: updatedAnswer,
        })
      );
    },
    [questionAnswer, questionDetails, dispatch]
  );

  const onAnswerChange = useCallback(
    (value: string | number | boolean | Date | Array<string>) => {
      switch (questionDetails.questionType) {
        case FormElementQuestionType.Bool:
          setQuestionAnswer(prev => ({
            ...prev,
            boolAnswer: value as boolean,
          }));
          break;
        case FormElementQuestionType.Date:
          setQuestionAnswer(prev => ({
            ...prev,
            dateAnswer: parseISO(value as string),
          }));
          break;
        case FormElementQuestionType.DateTime:
          setQuestionAnswer(prev => ({
            ...prev,
            dateTimeAnswer: parseISO(value as string),
          }));
          break;
        case FormElementQuestionType.Decimal:
          setQuestionAnswer(prev => ({
            ...prev,
            decimalAnswer: parseFloat(value as string),
          }));
          break;
        case FormElementQuestionType.Int:
          setQuestionAnswer(prev => ({
            ...prev,
            intAnswer: parseInt(value as string),
          }));
          break;
        case FormElementQuestionType.Radio:
          setQuestionAnswer(prev => ({
            ...prev,
            radioAnswer: value as string,
          }));
          break;
        case FormElementQuestionType.UnknownBool:
          setQuestionAnswer(prev => ({
            ...prev,
            unknownBoolOption: value as UnknownBoolAnswer,
          }));
          break;
        case FormElementQuestionType.KnownBool:
          setQuestionAnswer(prev => ({
            ...prev,
            knownBoolOption: value as KnownBoolAnswer,
          }));
          break;
        case FormElementQuestionType.InapplicableBool:
          setQuestionAnswer(prev => ({
            ...prev,
            inapplicableBoolOption: value as InapplicableBoolAnswer,
          }));
          break;
        case FormElementQuestionType.Text:
          setQuestionAnswer(prev => ({
            ...prev,
            textAnswer: value as string,
          }));
          break;
        case FormElementQuestionType.TextArea:
          setQuestionAnswer(prev => ({
            ...prev,
            textAreaAnswer: value as string,
          }));
          break;
        case FormElementQuestionType.MultiSelect:
          const multiSelectValue = Array.isArray(value) ? value : [value];
          setQuestionAnswer(prev => ({
            ...prev,
            multiSelectAnswerOptionList: multiSelectValue as string[],
          }));
          break;
        default:
          break;
      }
    },
    [questionDetails.questionType, setQuestionAnswer]
  );

  const inputCySelector = "QuestionnaireInput";

  const inputElement = useMemo(() => {
    let dateVal = "";
    const isFormEditable = formEditableStatus === ChecklistItemStatusType.Open;
    switch (questionDetails.questionType) {
      case FormElementQuestionType.Text:
        return (
          <TextInput
            data-cy={inputCySelector}
            name={questionDetails.id || ""}
            value={questionAnswer?.textAnswer || ""}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerChange(event.currentTarget.value)
            }
            onBlur={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerSubmit(event.currentTarget.value)
            }
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.Radio:
        const radioOptions =
          questionDetails.questionOptions
            ?.map(option => option.translations?.[0].value)
            .map(option => {
              return {
                value: option || "",
                label: option || "",
              };
            }) || [];
        const columnView = radioOptions?.length > 3;

        const answerOptionId = questionAnswer?.radioAnswer;
        const answerValue =
          questionDetails.questionOptions?.find(
            item => item.id === answerOptionId
          )?.translations?.[0].value || "";

        return (
          <RadioGroup
            className={classnames({
              [styles.booleanRadioLayout]: isRadioLayout,
              [styles.booleanColumnView]: columnView,
            })}
            showLabels={true}
            name={questionDetails.id || ""}
            value={answerValue}
            values={radioOptions || []}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const val = event.currentTarget.value;
              onAnswerChange(val);
              onAnswerSubmit(val);
            }}
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.UnknownBool:
        return (
          <RadioGroup
            className={classnames({
              [styles.booleanRadioLayout]: isRadioLayout,
            })}
            showLabels={true}
            name={questionDetails.id || ""}
            value={questionAnswer?.unknownBoolOption}
            values={unknownBoolOptions || []}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerSubmit(event.currentTarget.value)
            }
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.KnownBool:
        return (
          <RadioGroup
            className={classnames({
              [styles.booleanRadioLayout]: isRadioLayout,
            })}
            showLabels={true}
            name={questionDetails.id || ""}
            value={questionAnswer?.knownBoolOption}
            values={knownBoolOptions || []}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerSubmit(event.currentTarget.value)
            }
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.InapplicableBool:
        return (
          <RadioGroup
            className={classnames({
              [styles.booleanRadioLayout]: isRadioLayout,
            })}
            showLabels={true}
            name={questionDetails.id || ""}
            value={questionAnswer?.inapplicableBoolOption}
            values={inaplicableBoolOptions || []}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerSubmit(event.currentTarget.value)
            }
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.Bool:
        return (
          <CheckboxInput
            name={questionDetails.id || ""}
            value={questionAnswer?.boolAnswer ?? false}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerSubmit(event.currentTarget.value)
            }
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.Date:
        if (questionAnswer?.dateAnswer) {
          try {
            dateVal = format(
              parseISO(new Date(questionAnswer.dateAnswer)?.toISOString()),
              "yyyy-MM-dd"
            );
          } catch (error) {}
        }

        return (
          <DateInput
            data-cy={inputCySelector}
            name={questionDetails.id || ""}
            value={dateVal ?? ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const val = e.currentTarget.value;
              onAnswerChange(val);
              onAnswerSubmit(val);
            }}
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.DateTime:
        if (questionAnswer?.dateTimeAnswer) {
          try {
            dateVal = format(
              parseISO(new Date(questionAnswer.dateTimeAnswer)?.toISOString()),
              "yyyy-MM-dd"
            );
          } catch (error) {}
        }
        return (
          <DateInput
            data-cy={inputCySelector}
            type={"datetime-local"}
            name={questionDetails.id ?? ""}
            value={dateVal ?? ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const val = e.currentTarget.value;
              onAnswerChange(val);
              onAnswerSubmit(val);
            }}
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.Decimal:
        return (
          <NumberInput
            data-cy={inputCySelector}
            name={questionDetails.id || ""}
            value={questionAnswer?.decimalAnswer ?? null}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerChange(event.currentTarget.value)
            }
            onBlur={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerSubmit(event.currentTarget.value)
            }
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.Int:
        return (
          <NumberInput
            data-cy={inputCySelector}
            name={questionDetails.id || ""}
            value={questionAnswer?.intAnswer ?? null}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerChange(event.currentTarget.value)
            }
            onBlur={(event: ChangeEvent<HTMLInputElement>) =>
              onAnswerSubmit(event.currentTarget.value)
            }
            disabled={!isFormEditable}
          />
        );
      case FormElementQuestionType.MultiSelect:
        const options =
          questionDetails.questionOptions?.map(option => ({
            value: option.id || "",
            label: option.translations?.[0].value || "",
          })) ?? [];
        return (
          <Organisms.SelectInput
            position="top"
            selectedOptionCompare={({ value }) =>
              questionAnswer?.multiSelectAnswerOptionList?.includes(value) ??
              false
            }
            allowFilter={options.length > 8}
            name={questionDetails.id || ""}
            value={questionAnswer?.multiSelectAnswerOptionList ?? ""}
            onOptionChange={({ value }) => {
              const selectedOptions = Array.isArray(value) ? value : [value];

              onAnswerChange(selectedOptions);
              onAnswerSubmit(selectedOptions);
            }}
            optionsList={options}
            disabled={!isFormEditable}
            selectedOptionRender={option => (
              <div className={styles.selectValue}>{option.label}</div>
            )}
          >
            {option => (
              <div className={styles.selectOption}>{option.label}</div>
            )}
          </Organisms.SelectInput>
        );

      case FormElementQuestionType.TextArea:
        return (
          <TextareaInput
            data-cy={inputCySelector}
            className={styles.textarea}
            name={questionDetails.id || ""}
            value={questionAnswer?.textAreaAnswer || ""}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              onAnswerChange(event.currentTarget.value)
            }
            onBlur={(event: ChangeEvent<HTMLTextAreaElement>) =>
              onAnswerSubmit(event.currentTarget.value)
            }
            disabled={!isFormEditable}
          />
        );

      default:
        return (
          <div>
            {/* <TextInput
              name={name}
              value={questionAnswer?.textAnswer}
              onBlur={onAnswerSubmit}
              disabled={!isFormEditable}
            /> */}
          </div>
        );
    }
  }, [
    questionAnswer,
    questionDetails,
    formEditableStatus,
    unknownBoolOptions,
    knownBoolOptions,
    inaplicableBoolOptions,
    isRadioLayout,
    styles.booleanColumnView,
    styles.textarea,
    styles.booleanRadioLayout,
    styles.selectValue,
    styles.selectOption,
    onAnswerSubmit,
    onAnswerChange,
  ]);

  useEffect(() => {
    setQuestionAnswer(outsideQuestionAnswer);
  }, [outsideQuestionAnswer]);

  return <div className={styles.container}>{inputElement}</div>;
};

export { QuestionInput };
